import { type ReactNode, createContext, useCallback, useContext, useLayoutEffect, useMemo, useRef, useState } from 'react';

export interface WizardModalScreenActions {
    onContinue: () => void | Promise<void>;
    continueDisabled: boolean;
    continueLabel?: ReactNode;
}

interface WizardModalNavigationContextValue {
    screenActions: WizardModalScreenActions | null;
    // eslint-disable-next-line no-unused-vars
    registerScreenActions: (actions: WizardModalScreenActions | null) => void;
}

const WizardModalNavigationContext = createContext<WizardModalNavigationContextValue | null>(null);

export const WizardModalNavigationProvider = ({ children }: { children: React.ReactNode }) => {
    const [screenActions, setScreenActions] = useState<WizardModalScreenActions | null>(null);

    const registerScreenActions = useCallback((actions: WizardModalScreenActions | null) => {
        setScreenActions(actions);
    }, []);

    const value = useMemo(
        () => ({
            screenActions,
            registerScreenActions
        }),
        [registerScreenActions, screenActions]
    );

    return <WizardModalNavigationContext.Provider value={value}>{children}</WizardModalNavigationContext.Provider>;
};

export const useWizardModalScreenNavigation = (actions: WizardModalScreenActions) => {
    const context = useContext(WizardModalNavigationContext);

    if (!context) {
        throw new Error('useWizardModalScreenNavigation must be used within WizardModalNavigationProvider');
    }

    const { registerScreenActions } = context;
    const actionsRef = useRef(actions);
    actionsRef.current = actions;

    useLayoutEffect(() => {
        registerScreenActions({
            onContinue: () => actionsRef.current.onContinue(),
            continueDisabled: actionsRef.current.continueDisabled,
            continueLabel: actionsRef.current.continueLabel
        });

        return () => {
            registerScreenActions(null);
        };
    }, [actions.continueDisabled, registerScreenActions]);
};

export const useWizardModalNavigationState = () => {
    const context = useContext(WizardModalNavigationContext);

    if (!context) {
        throw new Error('useWizardModalNavigationState must be used within WizardModalNavigationProvider');
    }

    return context;
};
